<template>
    <div class="dropdown-cell" :class="field.alignment" @click="enableEdit">
        <template v-if="editing">
            <a-popover trigger="click" placement="bottomLeft" v-model="editing" @visibleChange="onVisibleChange">
                <template #content>
                    <div class="dropdown-options">
                        <a-input-search placeholder="Search" v-model="searchText" />
                        <div v-for="option in filteredOptions" :key="option.value" class="option-item" @click="onOptionClick(option.value)">
                            <a-checkbox v-if="isMultiple" :checked="selectedOptions.includes(option.value)" />
                            <div class="option-chip" :style="{ backgroundColor: option.color }">
                                {{ option.value }}
                            </div>
                        </div>
                    </div>
                </template>
                <div class="selected-options" ref="selectedOptionsRef">
                    <div v-for="option in selectedOptions" :key="option" class="option-chip" :style="{ backgroundColor: getOptionColor(option) }">
                        {{ option }}
                    </div>
                </div>
            </a-popover>
        </template>
        <template v-else>
            <div class="selected-options" ref="selectedOptionsRef">
                <div v-for="option in selectedOptions" :key="option" class="option-chip" :style="{ backgroundColor: getOptionColor(option) }">
                    {{ option }}
                </div>
            </div>
            <div class="chevron-icons">
                <a-icon type="up" />
                <a-icon type="down" />
            </div>
        </template>
    </div>
</template>

<script>
import Vue from "vue";

export default Vue.extend({
    name: "DropdownCell",
    props: {
        record: {
            type: Object,
            required: true,
        },
        field: {
            type: Object,
            required: true,
        },
    },
    data() {
        return {
            editing: false,
            searchText: "",
            selectedOptions: [],
        };
    },
    computed: {
        // ...
        isMultiple() {
            return this.field.options.dropdown.multiple;
        },
        dropdownOptions() {
            return this.field.options.dropdown.options;
        },
        filteredOptions() {
            return this.dropdownOptions.filter((option) =>
                option.value.toLowerCase().includes(this.searchText.toLowerCase())
            );
        },
    },
    methods: {
        enableEdit() {
            this.editing = true;
            this.selectedOptions = this.getSelectedOptions();
        },
        async disableEdit() {
            this.editing = false;
            try {
                const isChanged = !this.isEqual(this.record.data[this.field._id], this.selectedOptions);
                if (isChanged) {
                    await this.$emit("update-record", {
                        recordId: this.record._id,
                        fieldId: this.field._id,
                        value: this.isMultiple ? this.selectedOptions : this.selectedOptions[0],
                    });
                }
            } catch (error) {
                console.error(this.$t("tables.recordUpdatingError"), error);
                this.$message.error(this.$t("tables.recordUpdatingError"));
            }
        },
        isEqual(a, b) {
            if (this.isMultiple) {
                return Array.isArray(a) && Array.isArray(b) && a.length === b.length && a.every((val, index) => val === b[index]);
            } else {
                return a === b;
            }
        },
        updateRecord() {
            const updatedRecord = {
                ...this.record,
                [this.field._id]: this.isMultiple ? this.selectedOptions : this.selectedOptions[0],
            };
            this.$emit("update-record", updatedRecord);
        },
        getOptionColor(option) {
            const optionObj = this.dropdownOptions.find((opt) => opt.value === option);
            return optionObj ? optionObj.color : "";
        },
        getSelectedOptions() {
            const value = this.record.data[this.field._id];
            if (this.isMultiple) {
                return Array.isArray(value) ? value : [];
            } else {
                return value ? [value] : "";
            }
        },
        onOptionChange(option) {
            if (this.selectedOptions.includes(option)) {
                this.selectedOptions = this.selectedOptions.filter((opt) => opt !== option);
            } else {
                this.selectedOptions.push(option);
            }
        },
        onOptionClick(option) {
            if (this.isMultiple) {
                if (this.selectedOptions.includes(option)) {
                    this.selectedOptions = this.selectedOptions.filter((opt) => opt !== option);
                } else {
                    this.selectedOptions.push(option);
                }
            } else {
                this.selectedOptions = [option];
                this.disableEdit();
            }
        },
        async onVisibleChange(visible) {
            if (!visible) {
                await this.disableEdit();
            }
        },
    },
    mounted() {
        this.selectedOptions = this.getSelectedOptions();
    },
});
</script>

<style scoped lang="scss">
.dropdown-cell {
    position: relative;
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;

    .selected-options {
        display: flex;
        overflow-x: auto;
        white-space: nowrap;
        -ms-overflow-style: none;
        scrollbar-width: none;

        &::-webkit-scrollbar {
            display: none;
        }
    }

    .chevron-icons {
        position: absolute;
        top: 50%;
        right: 5px;
        transform: translateY(-50%);
        display: flex;
        flex-direction: column;
    }
}

.dropdown-options {
    max-height: 200px;
    overflow-y: auto;
}

.option-item {
    display: flex;
    align-items: center;
    padding: 4px 8px;
    cursor: pointer;

    &:hover {
        background-color: #f0f0f0;
    }

    .option-chip {
        margin-left: 8px;
    }
}

.option-chip {
    display: inline-block;
    padding: 2px 6px;
    border-radius: 16px;
    margin-right: 4px;
    cursor: pointer;
}
</style>
